// Deliberately its own module, with NO imports.
//
// This started in cognitoAuth.ts, which is what THROWS the error, so that is
// where it belonged conceptually. It could not stay there. Ten suites do
//
//     jest.mock('../cognitoAuth', () => ({ ...a few names... }))
//
// and a factory mock replaces the WHOLE module, so apiClient's import resolved
// to undefined and those suites died on
//
//     TypeError: (0, _cognitoAuth.isStaleCredentialError) is not a function
//
// at runtime rather than at compile time. Adding the name to all ten factories
// fixes it once and breaks again the next time anyone stubs cognitoAuth —
// silently, until CI. A pure predicate with no dependencies has no reason to
// sit behind the module people mock for its network calls.
/**
 * Is this error a credential that will NEVER work again, as opposed to a blip?
 *
 * Lives here because this module is what THROWS it: cognitoRequest sets
 * `message` from data.message and `code` from data.__type. Cognito puts the
 * exception TYPE in __type and a human sentence in message, so for a revoked
 * token the two are:
 *
 *   code:    "NotAuthorizedException"
 *   message: "Refresh Token has been revoked"
 *
 * Callers matched on `message` alone and therefore never saw the word
 * NotAuthorizedException at all. apiClient's classifier had no "revoked" in it
 * either, so a permanently dead token was read as TRANSIENT and retried — 7,567
 * requests and 115 MB of traffic against Cognito from one stranded browser,
 * while the user sat on a screen that could not recover.
 *
 * auth.ts had already been widened to include "revoked"; apiClient.ts had not.
 * That drift is why this is one exported function instead of a regex copied
 * into four places: matching on prose is guesswork, and the guess only has to
 * go stale once.
 *
 * `code` is checked first because it is the field with a fixed vocabulary.
 */
export function isStaleCredentialError(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null | undefined;
  const code = typeof e?.code === 'string' ? e.code : '';
  const message = typeof e?.message === 'string' ? e.message : '';
  if (/NotAuthorizedException|InvalidParameterException|UserNotFoundException|TokenRevoked/i.test(code)) return true;
  return /NotAuthorizedException|InvalidParameterException|invalid.*(refresh|token)|(refresh|token).*(invalid|expired|revoked|not valid)|expired|revoked/i.test(message);
}
